import { DatasetDropdown } from "components/basic/dropdowns/dataset/DatasetDropdown";
import { DashboardEditorProps } from "components/ComponentTypeTypes";
import { Divider } from "primereact/divider";
import DatasetMultiSelect from "components/basic/dropdowns/dataset/DatasetMultiSelect";
import { Dropdown } from "primereact/dropdown";
import { TabPanel, TabView } from "primereact/tabview";
import { InputSwitch } from "primereact/inputswitch";
import { InputNumber } from "primereact/inputnumber";
import { toInstant } from "utils/time-utils";
import { InputText } from "primereact/inputtext";
import CalendarWrapper from "components/basic/calendar-wrapper/CalendarWrapper";
import React, { FC, useEffect, useState } from "react";
import { defaultLoadConf } from "components/dashboard/data-input-output-table/defaultConfigs";
import { T } from "services/lang/LanguageService";
import LoadConfigInputs from "components/dashboard/charts/LoadConfigInputs";
import { DatasetLoadingConf } from "components/dashboard/charts/interfaces";
import {
  aggregationPeriodOptions,
  DataDisplayType,
  dataDisplayTypeOptions,
  diffTypeOptions,
  ValueChangeIndicatorProps,
} from "../ValueChangeIndicatorTypes";

const ValueChangeIndicatorEditor: FC<DashboardEditorProps<ValueChangeIndicatorProps>> = ({
  config,
  onConfigChange,
}) => {
  const [currentConfig, setCurrentConfig] = useState<ValueChangeIndicatorProps>({
    ...config,
    loadConf: config.loadConf ?? defaultLoadConf,
  });

  useEffect(() => {
    onConfigChange(currentConfig);
  }, [currentConfig]);

  const currentMultiSelectDatasets = currentConfig.datasets?.slice(1) ?? [];

  const handleDatasetChange = (value: string | null | string[], index: number) => {
    const updated = [...(currentConfig.datasets ?? [])];

    if (value === null) {
      updated.splice(index, 1);
    } else if (Array.isArray(value)) {
      const primary = updated[0] ?? "";
      updated.length = 0;
      updated.push(primary, ...value);
    } else {
      updated[index] = value;
    }

    setCurrentConfig({ ...currentConfig, datasets: updated });
  };

  const handleMultiDatasetChange = (value: string[]) => {
    const primary = currentConfig.datasets[0] ?? "";
    const uniqueValues = [...new Set(value.filter(v => v !== primary))];
    setCurrentConfig({ ...currentConfig, datasets: [primary, ...uniqueValues] });
  };

  const handleDisplayLastValueDiffChange = (value: boolean) => {
    if (value) {
      setCurrentConfig({
        ...currentConfig,
        displayLastValueDiff: value,
        datasets: [currentConfig.datasets[0]],
      });
      return;
    }
    setCurrentConfig({ ...currentConfig, displayLastValueDiff: value });
  };

  const getDatasetSelectionInput = () => {
    if (currentConfig.displayInline) {
      return (
        <div className="flex flex-column gap-2 w-8">
          <label htmlFor="valueDataset">{T("word.dataset")}</label>
          <DatasetDropdown
            value={currentConfig.datasets?.[1] ?? ""}
            onChange={val => handleDatasetChange(val, 1)}
          />
        </div>
      );
    }
    return (
      <div className="flex flex-column gap-2 w-8">
        <label htmlFor="valueDataset">{T("word.datasets")}</label>
        <DatasetMultiSelect value={currentMultiSelectDatasets} onChange={handleMultiDatasetChange} />
      </div>
    );
  };

  return (
    <div className="flex flex-column">
      <TabView>
        <TabPanel header={T("word.general")}>
          <div className="flex flex-row gap-3">
            <div className="flex flex-column gap-2 w-6 mb-3">
              <label htmlFor="valueDataset">{T("word.dataset")}</label>
              <DatasetDropdown
                value={currentConfig.datasets?.[0] ?? ""}
                onChange={val => handleDatasetChange(val, 0)}
              />
            </div>
            <div className="flex flex-column gap-2 w-3">
              <label htmlFor="dataDisplayType">{T("label.data-display-type")}</label>
              <Dropdown
                id="dataDisplayType"
                value={currentConfig.dataDisplayType ?? DataDisplayType.LAST_VALUE}
                options={dataDisplayTypeOptions}
                onChange={e => setCurrentConfig({ ...currentConfig, dataDisplayType: e.value })}
              />
            </div>
            {currentConfig.dataDisplayType === DataDisplayType.SUM_OF_VALUES && (
              <div className="flex flex-column gap-2">
                <label htmlFor="upToRefTime">{T("label.up-to-ref-time")}</label>
                <InputSwitch
                  id="upToRefTime"
                  checked={currentConfig.upToRefTime ?? false}
                  onChange={e => setCurrentConfig({ ...currentConfig, upToRefTime: e.value })}
                />
              </div>
            )}
            {currentConfig.dataDisplayType === DataDisplayType.LAST_VALUE && (
              <div className="flex flex-column gap-2">
                <label htmlFor="skipMonthCheck">{T("label.skip-month-check")}</label>
                <InputSwitch
                  id="skipMonthCheck"
                  checked={currentConfig.skipMonthCheck ?? false}
                  onChange={e =>
                    setCurrentConfig({
                      ...currentConfig,
                      skipMonthCheck: e.value,
                    })
                  }
                />
              </div>
            )}
          </div>
          <div className="flex flex-row gap-3">
            {currentConfig.dataDisplayType === DataDisplayType.HORIZONTAL_BAR_CHART && (
              <div className="flex flex-column gap-2">
                <label htmlFor="aggregationPeriod">{T("label.aggregation-period")}</label>
                <Dropdown
                  id="aggregationPeriod"
                  value={currentConfig.aggregationPeriod ?? ""}
                  options={aggregationPeriodOptions}
                  onChange={e => setCurrentConfig({ ...currentConfig, aggregationPeriod: e.value })}
                />
              </div>
            )}
            {currentConfig.dataDisplayType === DataDisplayType.HORIZONTAL_BAR_CHART ||
              (currentConfig.dataDisplayType === DataDisplayType.WEEK_YEAR_COMPARISON &&
                currentConfig.useHorizontalBarChart && (
                  <div className="flex flex-column gap-2 w-2">
                    <label htmlFor="upToUnit">{T("label.up-to")}</label>
                    <Dropdown
                      id="upToUnit"
                      value={currentConfig.upToUnit ?? ""}
                      onChange={e =>
                        setCurrentConfig({
                          ...currentConfig,
                          upToUnit: e.value,
                        })
                      }
                      options={aggregationPeriodOptions}
                    />
                  </div>
                ))}
            {!currentConfig.displayLastValueDiff &&
              currentConfig.dataDisplayType !== DataDisplayType.HORIZONTAL_BAR_CHART && (
                <div className="flex flex-column gap-2">
                  <label htmlFor="displayInline">{T("label.display-inline")}</label>
                  <InputSwitch
                    id="displayInline"
                    checked={currentConfig.displayInline ?? false}
                    onChange={e => setCurrentConfig({ ...currentConfig, displayInline: e.value })}
                  />
                </div>
              )}
            <div className="flex flex-column gap-2">
              <label htmlFor="hideUnit">{T("action.hide-unit")}</label>
              <InputSwitch
                id="hideUnit"
                checked={currentConfig.hideUnit ?? false}
                onChange={e => setCurrentConfig({ ...currentConfig, hideUnit: e.value })}
              />
            </div>
            {config.dataDisplayType === DataDisplayType.WEEK_YEAR_COMPARISON ? (
              <div className="flex flex-column gap-2">
                <label htmlFor="useDataUpToLastFinishedWeek">
                  {T("load-conf.use-data-up-to-last-finished-week")}
                </label>
                <InputSwitch
                  id="useDataUpToLastFinishedWeek"
                  checked={config.useDataUpToLastFinishedWeek ?? false}
                  onChange={e => onConfigChange({ ...config, useDataUpToLastFinishedWeek: e.value })}
                />
              </div>
            ) : (
              <div className="flex flex-column gap-2">
                <label htmlFor="useDataUpToLastFinishedMonth">
                  {T("load-conf.use-data-up-to-last-finished-month")}
                </label>
                <InputSwitch
                  id="useDataUpToLastFinishedMonth"
                  checked={config.useDataUpToLastFinishedMonth ?? false}
                  onChange={e => onConfigChange({ ...config, useDataUpToLastFinishedMonth: e.value })}
                />
              </div>
            )}
            <div className="flex flex-column gap-2">
              <label htmlFor="hideIfValueIsZeroOrMore">{T("label.hide-if-value-is-zero-or-more")}</label>
              <InputSwitch
                id="hideIfValueIsZeroOrMore"
                checked={currentConfig.hideIfValueIsZeroOrMore ?? false}
                onChange={e => setCurrentConfig({ ...currentConfig, hideIfValueIsZeroOrMore: e.value })}
              />
            </div>
            {config.dataDisplayType === DataDisplayType.WEEK_YEAR_COMPARISON && (
              <div className="flex flex-column gap-2">
                <label htmlFor="useHorizontalBarChart">{T("action.use-horizontal-bar-chart")}</label>
                <InputSwitch
                  id="useHorizontalBarChart"
                  checked={currentConfig.useHorizontalBarChart ?? false}
                  onChange={e => setCurrentConfig({ ...currentConfig, useHorizontalBarChart: e.value })}
                />
              </div>
            )}
          </div>
          <Divider />
          <div className="flex flex-row gap-3 justify-content-between">
            {currentConfig.dataDisplayType !== DataDisplayType.AVERAGE_VALUE && (
              <div className="flex flex-column gap-2 w-4">
                <label htmlFor="displayLastValueDiff">{T("action.display-last-value-difference")}</label>
                <InputSwitch
                  id="displayLastValueDiff"
                  checked={currentConfig.displayLastValueDiff ?? false}
                  onChange={e => handleDisplayLastValueDiffChange(e.value)}
                />
              </div>
            )}
            {!currentConfig.displayLastValueDiff &&
            currentConfig.dataDisplayType !== DataDisplayType.HORIZONTAL_BAR_CHART &&
            currentConfig.dataDisplayType !== DataDisplayType.WEEK_YEAR_COMPARISON &&
            currentConfig.dataDisplayType !== DataDisplayType.MONTH_YEAR_COMPARISON ? (
              <>{getDatasetSelectionInput()}</>
            ) : (
              <>
                <div className="flex flex-column gap-2 w-3">
                  <label htmlFor="useDiffIndicatorColors">{T("action.use-diff-indicator-colors")}</label>
                  <InputSwitch
                    id="useDiffIndicatorColors"
                    checked={currentConfig.useDiffIndicatorColors ?? false}
                    onChange={e => setCurrentConfig({ ...currentConfig, useDiffIndicatorColors: e.value })}
                  />
                </div>
                <div className="flex flex-column gap-2 w-2">
                  <label htmlFor="diffType">{T("word.diff-type")}</label>
                  <Dropdown
                    id="diffType"
                    value={currentConfig.diffType}
                    options={diffTypeOptions}
                    onChange={e => setCurrentConfig({ ...currentConfig, diffType: e.value })}
                    placeholder={T("word.select-one")}
                    className="p-inputtext-sm"
                  />
                </div>
                <div className="flex flex-column gap-2 w-3">
                  <label htmlFor="diffValueDecimalPlaces">{T("word.diff-decimal-places")}</label>
                  <InputNumber
                    id="diffValueDecimalPlaces"
                    value={currentConfig.diffValueDecimalPlaces as number}
                    onValueChange={e =>
                      setCurrentConfig({ ...currentConfig, diffValueDecimalPlaces: e.value ?? 0 })
                    }
                  />
                </div>
              </>
            )}
          </div>
          {currentConfig.dataDisplayType !== DataDisplayType.LAST_VALUE && (
            <>
              <Divider />
              <div className="flex flex-row gap-2">
                <LoadConfigInputs
                  loadConf={currentConfig.loadConf as DatasetLoadingConf}
                  handleLoadConfChange={e =>
                    setCurrentConfig(prev => ({
                      ...prev,
                      loadConf: e,
                    }))
                  }
                />
              </div>
            </>
          )}
          <Divider />
          <div className="flex flex-row gap-2">
            <div className="flex flex-column w-2 gap-2">
              <label htmlFor="decimalPlaces">{T("config.decimal-places")}</label>
              <InputNumber
                id="decimalPlaces"
                value={currentConfig.decimalPlaces}
                onChange={e => setCurrentConfig({ ...currentConfig, decimalPlaces: e.value ?? 2 })}
                className="p-inputtext-sm"
                placeholder={T("config.decimal-places")}
              />
            </div>
            {currentConfig.dataDisplayType !== DataDisplayType.HORIZONTAL_BAR_CHART && (
              <div className="flex flex-column gap-2 w-2">
                <label htmlFor="highlightValue">{T("action.highlight-value")}</label>
                <InputSwitch
                  id="highlightValue"
                  checked={currentConfig.highlightValue ?? false}
                  onChange={e => setCurrentConfig({ ...currentConfig, highlightValue: e.value })}
                />
              </div>
            )}
            <div className="w-2 gap-2 flex flex-column justify-content-between">
              <label htmlFor="updateFrequency">{T("load-conf.update-frequency")}</label>
              <div className="p-inputgroup w-full">
                <InputNumber
                  id="updateFrequency"
                  value={currentConfig.updateFrequency as number}
                  onValueChange={e => setCurrentConfig({ ...currentConfig, updateFrequency: e.value ?? 0 })}
                />
                <span className="p-inputgroup-addon">s</span>
              </div>
            </div>
            <div className="flex flex-column gap-2 w-3">
              <label htmlFor="convertToAbsoluteValues">{T("action.convert-to-absolute-values")}</label>
              <InputSwitch
                id="convertToAbsoluteValues"
                checked={currentConfig.convertToAbsoluteValues ?? false}
                onChange={e => setCurrentConfig({ ...currentConfig, convertToAbsoluteValues: e.value })}
              />
            </div>
            <div className="flex flex-column gap-2 w-2">
              <label htmlFor="hideZeroValues">{T("action.hide-zero-values")}</label>
              <InputSwitch
                id="hideZeroValues"
                checked={currentConfig.hideZeroValues ?? false}
                onChange={e => setCurrentConfig({ ...currentConfig, hideZeroValues: e.value })}
              />
            </div>
          </div>
          <Divider />
          <div className="flex flex-row gap-2">
            <div className="flex flex-column gap-2 w-3">
              <label htmlFor="useStartOfTheProject">{T("action.use-start-of-the-project")}</label>
              <InputSwitch
                id="useStartOfTheProject"
                checked={currentConfig.useStartOfTheProject ?? false}
                onChange={e => setCurrentConfig({ ...currentConfig, useStartOfTheProject: e.value })}
              />
            </div>
            <div className="flex flex-column gap-2 w-3">
              <label htmlFor="startOfTheProject">{T("label.start-of-the-project")}</label>
              <CalendarWrapper
                id="startOfTheProject"
                value={
                  currentConfig.startOfTheProject
                    ? new Date(currentConfig.startOfTheProject.toString())
                    : new Date()
                }
                onChange={e => {
                  setCurrentConfig({ ...currentConfig, startOfTheProject: toInstant(e) });
                }}
              />
            </div>
          </div>
        </TabPanel>
        <TabPanel header={T("label.title-settings")}>
          <div className="flex flex-column gap-2">
            <div className="flex flex-row gap-2">
              <div className="flex flex-column w-4 gap-2">
                <label htmlFor="title">{T("word.title")}</label>
                <InputText
                  id="title"
                  value={currentConfig.title}
                  onChange={e => setCurrentConfig({ ...currentConfig, title: e.target.value })}
                  className="p-inputtext-sm"
                  placeholder={T("action.write-title")}
                />
              </div>
              <div className="flex flex-column w-4 gap-2">
                <label htmlFor="subtitle">{T("word.subtitle")}</label>
                <InputText
                  id="subtitle"
                  value={currentConfig.subtitle}
                  onChange={e => setCurrentConfig({ ...currentConfig, subtitle: e.target.value })}
                  className="p-inputtext-sm"
                  placeholder={T("action.write-subtitle")}
                />
              </div>
            </div>
          </div>
        </TabPanel>
      </TabView>
    </div>
  );
};

export default ValueChangeIndicatorEditor;
